import {shareRecordCard} from '../lib/share-card.js';
import {usePlatform} from '../lib/platform-context.js';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DayMark, PhotoDay, WaterLog, Pet } from '@petrhythm/core';
import { liveQuery, repository } from '@petrhythm/data';
import { Button } from '@petrhythm/ui';
import { formatShortDay, todayKst } from '../lib/format.js';
import { track } from '../lib/platform.js';

/**
 * 오늘 화면 아래쪽 한 줄 도구: K3 별일 없던 날 표시, K15 오늘 사진, K8 남은 물 적기 안내.
 * 바텀시트·대화상자로 띄우지 않고 본문 안에만 둔다(토스 비게임 가이드).
 */
export function TodayExtras({ petId, petName, onChanged }: { petId: string; petName: string; onChanged: () => void }) {
  const today = todayKst();
  const [mark, setMark] = useState<DayMark | null>(null); const [photo, setPhoto] = useState<PhotoDay | null>(null); const [pendingWater, setPendingWater] = useState<WaterLog | null>(null);
  const [message, setMessage] = useState(''); const [busy, setBusy] = useState(false);
  const dismissKey = `petrhythm.water-left-dismissed.${petId}`;
  useEffect(() => {
    const sub = liveQuery(() => Promise.all([repository.list<DayMark>('dayMarks'), repository.list<PhotoDay>('photoDays'), repository.list<WaterLog>('waterLogs')])).subscribe({ next: ([marks, photos, water]) => {
      setMark(marks.find((row) => row.petId === petId && row.date === today && row.kind === 'quiet') ?? null);
      setPhoto(photos.find((row) => row.petId === petId && row.date === today) ?? null);
      let dismissed = ''; try { dismissed = localStorage.getItem(dismissKey) ?? ''; } catch { /* 안내는 다시 보여도 된다 */ }
      setPendingWater(water.filter((row) => row.petId === petId && row.leftMl == null && row.date < today && row.id !== dismissed).sort((a, b) => b.date.localeCompare(a.date))[0] ?? null);
    }, error: () => undefined });
    return () => sub.unsubscribe();
  }, [petId, today]);
  const toggleQuiet = async () => {
    if (busy) return; setBusy(true);
    try {
      if (mark) { await repository.softDelete<DayMark>('dayMarks', mark.id, mark.updatedAt); setMessage('표시를 되돌렸어요.'); }
      else { await repository.put('dayMarks', { petId, date: today, kind: 'quiet' }); track('record_saved', { recordType: 'quiet_day' }); setMessage('오늘은 별일 없었다고 남겼어요. 진료 첫 장에서 "확인한 날"로 따로 세요.'); }
      onChanged();
    } catch { setMessage('저장하지 못했어요. 다시 눌러 주세요.'); }
    finally { setBusy(false); }
  };
  const dismissWater = () => { if (!pendingWater) return; try { localStorage.setItem(dismissKey, pendingWater.id); } catch { /* 이 화면에서만 닫힘 */ } setPendingWater(null); };
  return <section className="today-extras" aria-label="오늘 한 번에 남기기">
    {pendingWater ? <div className="water-reminder" role="note"><p>{formatShortDay(pendingWater.date)}에 채운 물 {pendingWater.filledMl}ml · 아직 남은 양을 안 적었어요.</p><div className="button-row"><Link className="ng-button ng-button-secondary" to={`/app/record/water/${petId}`}>남은 양 적기</Link><Button variant="ghost" onClick={dismissWater}>닫기</Button></div></div> : null}
    <div className="today-extras-row">
      <Button variant={mark ? 'secondary' : 'ghost'} aria-pressed={Boolean(mark)} disabled={busy} onClick={() => void toggleQuiet()}>{mark ? '별일 없음 표시됨 · 되돌리기' : '오늘 별일 없었어요'}</Button>
      <Link className="ng-button ng-button-ghost" to={`/app/record/photo/${petId}`}>{photo ? '오늘 사진 보기' : `${petName} 오늘 사진`}</Link>
    </div>
    {message ? <p role="status" className="inline-message">{message}</p> : null}
  </section>;
}

export function TogetherDay({pet}:{pet:Pet}){const platform=usePlatform();const [message,setMessage]=useState('');const today=todayKst();const match=[pet.birthDate,pet.adoptedAt].some(d=>d&&d.slice(5)===today.slice(5));if(!match)return null;return <section className="ng-card"><h2>{pet.name}와 함께한 날</h2><Button onClick={()=>void shareRecordCard(platform,pet).then(setMessage).catch(e=>setMessage(e.message))}>함께한 날 카드 보내기</Button>{message?<p role="status">{message}</p>:null}</section>;}
